import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import DeviceSelector from './DeviceSelector.jsx'
import DeviceStatusIndicators from './DeviceStatusIndicators.jsx'
import UserMenu from './UserMenu.jsx'
import NotificationBell from './NotificationBell.jsx'
import '../styles/enhanced-ui.css'

/**
 * Optimized scroll handler with throttling for performance
 */
const useScrollState = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  
  useEffect(() => {
    let ticking = false
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10)
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return isScrolled
}

/**
 * Memoized brand section for performance optimization
 */
const BrandSection = memo(({ onClick }) => (
  <div className="navbar-brand-container-world-class">
    <button 
      className="navbar-brand-world-class"
      onClick={onClick}
      type="button"
      aria-label="Revatek Connect - Return to home"
    >
      <img 
        src="/images/Revatek-logo.svg" 
        alt="Revatek" 
        width="140" 
        height="auto"
        loading="lazy"
        decoding="async"
        className="navbar-brand-logo-world-class"
      />
    </button>
    
    <div className="page-context-all-screens">
      <h1 className="page-title-world-class">
        Connect
        <span className="beta-badge">Beta</span>
      </h1>
    </div>
  </div>
))

BrandSection.displayName = 'BrandSection'

/**
 * Memoized device context section for performance
 */
const DeviceContext = memo(({ isFleetView, selectedDevice }) => (
  <div className="device-context-world-class">
    {isFleetView ? (
      <div className="fleet-indicator-world-class">
        <i className="bi bi-collection-fill fleet-icon"></i>
        <span className="fleet-text">Fleet Overview</span>
        <div className="fleet-badge"></div>
      </div>
    ) : (
      <>
        <DeviceSelector />
        {selectedDevice && (
          <div className="status-indicators-world-class">
            <DeviceStatusIndicators />
          </div>
        )}
      </>
    )}
  </div>
))

DeviceContext.displayName = 'DeviceContext'

/**
 * Memoized control section for performance
 */
const ControlSection = memo(() => (
  <div className="navbar-controls-world-class">
    <div className="control-item-world-class">
      <NotificationBell />
    </div>
    
    <div className="control-item-world-class">
      <UserMenu />
    </div>
  </div>
))

ControlSection.displayName = 'ControlSection'

/**
 * World-Class TopNavigation component optimized for React 19+
 * Features:
 * - Premium Revatek design with smooth animations
 * - Smart device context management
 * - Real-time status indicators
 * - Performance optimized with memo and proper state management
 * - Accessibility compliant (WCAG 2.1 AA)
 */
const TopNavigation = memo(() => {
  const navigate = useNavigate()
  const { hasDevices, isFleetView, selectedDevice, isAuthenticated } = useCloudContext()
  const isScrolled = useScrollState()

  // Memoized navigation classes for performance
  const navbarClasses = useMemo(() => [
    'navbar',
    'navbar-expand-lg', 
    'sticky-top',
    'top-navbar-world-class',
    isScrolled ? 'navbar-scrolled' : '',
    'smooth-transition',
  ].filter(Boolean).join(' '), [isScrolled])

  // Memoized navigation handler
  const handleLogoClick = useCallback(() => {
    const targetRoute = isFleetView ? '/fleet' : hasDevices ? '/dashboard' : '/'
    navigate(targetRoute)
  }, [navigate, isFleetView, hasDevices])

  return (
    <nav className={navbarClasses} role="navigation" aria-label="Main navigation">
      <div className="container-fluid">
        <BrandSection onClick={handleLogoClick} />
        
        {isAuthenticated && hasDevices && (
          <DeviceContext isFleetView={isFleetView} selectedDevice={selectedDevice} />
        )}
        
        {isAuthenticated && <ControlSection />}
      </div>
    </nav>
  )
})

TopNavigation.displayName = 'TopNavigation'

export default TopNavigation
